"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const Utils_1 = require("./Utils");
const Binding_CopyOnWriteArray_1 = require("./Binding.CopyOnWriteArray");
const Binding_CopyOnWriteSet_1 = require("./Binding.CopyOnWriteSet");
const Binding_CopyOnWriteMap_1 = require("./Binding.CopyOnWriteMap");
const Record_1 = require("./Record");
const Handle_1 = require("./Handle");
const Snapshot_1 = require("./Snapshot");
const Config_1 = require("../Config");
// Config
exports.RT_CONFIG = Symbol("RT:CONFIG");
exports.RT_CLASS = Symbol("RT:CLASS");
const EMPTY_CONFIG_TABLE = {};
const DEFAULT = {
    stateful: false,
    latency: Config_1.Renew.NoCache,
    reentrant: Config_1.ReentrantCall.WaitAndRestart,
    separate: Config_1.SeparateFrom.Reaction,
    monitor: null,
    trace: undefined,
};
class ConfigRecord {
    constructor(body, existing, patch, implicit) {
        this.body = body !== undefined ? body : existing.body;
        this.stateful = merge(DEFAULT.stateful, existing.stateful, patch.stateful, implicit);
        this.latency = merge(DEFAULT.latency, existing.latency, patch.latency, implicit);
        this.reentrant = merge(DEFAULT.reentrant, existing.reentrant, patch.reentrant, implicit);
        this.separate = merge(DEFAULT.separate, existing.separate, patch.separate, implicit);
        this.monitor = merge(DEFAULT.monitor, existing.monitor, patch.monitor, implicit);
        this.trace = merge(DEFAULT.trace, existing.trace, patch.trace, implicit);
        Object.freeze(this);
    }
}
exports.ConfigRecord = ConfigRecord;
ConfigRecord.default = new ConfigRecord(Utils_1.undef, Object.assign({ body: Utils_1.undef }, DEFAULT), {}, false);
function merge(def, existing, patch, implicit) {
    return patch !== undefined && (existing === def || !implicit) ? patch : existing;
}
// Virtualization
class Virt {
    getPrototypeOf(h) {
        return Reflect.getPrototypeOf(h.stateless);
    }
    get(h, prop, receiver) {
        let value;
        const config = Virt.getConfig(h.stateless, prop);
        if (!config || (config.body === decoratedfield && config.stateful)) { // versioned state
            const r = Snapshot_1.Snapshot.current(false).read(h);
            value = r.data[prop];
            if (value === undefined && !r.data.hasOwnProperty(prop))
                value = Reflect.get(h.stateless, prop, receiver);
            else
                Record_1.Record.markViewed(r, prop);
        }
        else
            value = Reflect.get(h.stateless, prop, receiver);
        return value;
    }
    set(h, prop, value, receiver) {
        const config = Virt.getConfig(h.stateless, prop);
        if (!config || (config.body === decoratedfield && config.stateful)) { // versioned state
            const r = Snapshot_1.Snapshot.current(true).tryWrite(h, prop, value);
            if (r !== Record_1.Record.empty) { // empty when r.data[prop] === value, thus creation of changing record was skipped
                r.data[prop] = value;
                const v = r.prev.record.data[prop];
                Record_1.Record.markChanged(r, prop, !Utils_1.Utils.equal(v, value), value);
            }
        }
        else
            h.stateless[prop] = value;
        return true;
    }
    getOwnPropertyDescriptor(h, prop) {
        const r = Snapshot_1.Snapshot.current(false).read(h);
        return Reflect.getOwnPropertyDescriptor(r.data, prop);
    }
    ownKeys(h) {
        // TODO: Better implementation to avoid filtering
        const r = Snapshot_1.Snapshot.current(false).read(h);
        const result = [];
        for (const prop of Object.getOwnPropertyNames(r.data)) {
            const value = r.data[prop];
            if (typeof (value) !== "object" || value.constructor.name !== "CachedResult")
                result.push(prop);
        }
        return result;
    }
    static decorateClass(implicit, config, origCtor) {
        let ctor = origCtor;
        const stateful = config.stateful || false;
        if (stateful) {
            ctor = function (...args) {
                const stateless = new origCtor(...args);
                const h = Virt.createHandle(stateful, stateless, undefined);
                return h.proxy;
            };
            Object.setPrototypeOf(ctor, Object.getPrototypeOf(origCtor)); // preserve prototype
            Object.defineProperties(ctor, Object.getOwnPropertyDescriptors(origCtor)); // preserve static definitions
        }
        Virt.applyConfig(ctor.prototype, exports.RT_CLASS, decoratedclass, config, implicit);
        return ctor;
    }
    static decorateField(implicit, config, target, prop) {
        config = Virt.applyConfig(target, prop, decoratedfield, config, implicit);
        if (config.stateful) {
            const get = function () {
                const h = Virt.acquireHandle(this);
                return Virt.proxy.get(h, prop, this);
            };
            const set = function (value) {
                const h = Virt.acquireHandle(this);
                return Virt.proxy.set(h, prop, value, this);
            };
            const enumerable = true;
            const configurable = false;
            return Object.defineProperty(target, prop, { get, set, enumerable, configurable });
        }
    }
    static decorateMethod(implicit, config, type, method, pd) {
        const enumerable = pd ? pd.enumerable === true : /* istanbul ignore next */ true;
        const configurable = true;
        const methodConfig = Virt.applyConfig(type, method, pd.value, config, implicit);
        const get = function () {
            const classConfig = Virt.getConfig(Object.getPrototypeOf(this), exports.RT_CLASS) || ConfigRecord.default;
            const h = classConfig.stateful ? Utils_1.Utils.get(this, Handle_1.RT_HANDLE) : Virt.acquireHandle(this);
            const value = Virt.createCachedMethodTrap(h, method, methodConfig);
            Object.defineProperty(h.stateless, method, { value, enumerable, configurable });
            return value;
        };
        return Object.defineProperty(type, method, { get, enumerable, configurable });
    }
    static applyConfig(target, prop, body, config, implicit) {
        const table = Virt.acquireConfigTable(target);
        const existing = table[prop] || ConfigRecord.default;
        const result = table[prop] = new ConfigRecord(body, existing, config, implicit);
        return result;
    }
    static acquireConfigTable(target) {
        let table = target[exports.RT_CONFIG];
        if (!target.hasOwnProperty(exports.RT_CONFIG)) {
            table = Object.setPrototypeOf({}, table || {});
            Utils_1.Utils.set(target, exports.RT_CONFIG, table);
        }
        return table;
    }
    static getConfigTable(target) {
        return target[exports.RT_CONFIG] || /* istanbul ignore next */ EMPTY_CONFIG_TABLE;
    }
    static getConfig(target, prop) {
        return Virt.getConfigTable(target)[prop];
    }
    static acquireHandle(obj) {
        if (obj !== Object(obj) || Array.isArray(obj)) /* istanbul ignore next */
            throw new Error("only objects can be reactive");
        let h = Utils_1.Utils.get(obj, Handle_1.RT_HANDLE);
        if (!h) {
            h = new Handle_1.Handle(obj, obj, Virt.proxy);
            Utils_1.Utils.set(obj, Handle_1.RT_HANDLE, h);
            Virt.decorateField(false, { stateful: true }, obj, Record_1.RT_UNMOUNT);
        }
        return h;
    }
    static createHandle(stateful, stateless, proxy) {
        const h = new Handle_1.Handle(stateless, proxy, Virt.proxy);
        const r = Snapshot_1.Snapshot.current(true).write(h, Handle_1.RT_HANDLE, Handle_1.RT_HANDLE);
        Utils_1.Utils.set(r.data, Handle_1.RT_HANDLE, h);
        initRecordData(h, stateful, stateless, r);
        return h;
    }
}
exports.Virt = Virt;
Virt.proxy = new Virt();
/* istanbul ignore next */
Virt.createCachedMethodTrap = function (h, prop, config) {
    throw new Error("createCachedMethodTrap should never be called");
};
function initRecordData(h, stateful, stateless, record) {
    const configTable = Virt.getConfigTable(Object.getPrototypeOf(stateless));
    const r = Snapshot_1.Snapshot.current(true).write(h, Handle_1.RT_HANDLE, Handle_1.RT_HANDLE);
    for (const prop of Object.getOwnPropertyNames(stateless))
        initRecordProp(stateful, configTable, prop, r, stateless);
    for (const prop of Object.getOwnPropertySymbols(stateless)) /* istanbul ignore next */
        initRecordProp(stateful, configTable, prop, r, stateless);
}
function initRecordProp(stateful, configTable, prop, r, stateless) {
    if (stateful && configTable[prop] !== false) {
        const value = Utils_1.Utils.copyProp(stateless, r.data, prop);
        Record_1.Record.markChanged(r, prop, true, value);
    }
}
/* istanbul ignore next */
function decoratedfield(...args) {
    throw new Error("decoratedfield should never be called");
}
/* istanbul ignore next */
function decoratedclass(...args) {
    throw new Error("decoratedclass should never be called");
}
class CopyOnWrite {
    get(binding, prop, receiver) {
        const a = binding.readable(receiver);
        return a[prop];
    }
    set(binding, prop, value, receiver) {
        const a = binding.writable(receiver);
        return a[prop] = value;
    }
    static seal(data, proxy, prop) {
        const value = data[prop];
        if (Array.isArray(value)) {
            if (!Object.isFrozen(value))
                data[prop] = new Proxy(Binding_CopyOnWriteArray_1.CopyOnWriteArray.seal(proxy, prop, value), CopyOnWrite.global);
        }
        else if (value instanceof Set) {
            if (!Object.isFrozen(value))
                data[prop] = new Proxy(Binding_CopyOnWriteSet_1.CopyOnWriteSet.seal(proxy, prop, value), CopyOnWrite.global);
        }
        else if (value instanceof Map) {
            if (!Object.isFrozen(value))
                data[prop] = new Proxy(Binding_CopyOnWriteMap_1.CopyOnWriteMap.seal(proxy, prop, value), CopyOnWrite.global);
        }
    }
}
exports.CopyOnWrite = CopyOnWrite;
CopyOnWrite.global = new CopyOnWrite();
